/**
 * Generate Program Screen (web / admin)
 *
 * Admin selects one or two clients, then generates an individualized lifestyle program
 * markdown. Template (men/women) is derived from the selected client's profile gender.
 * Result can be viewed, downloaded as .md, or copied to clipboard.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View, useWindowDimensions, } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useGetMeQuery, useGetUsersQuery, useGenerateProgramMutation, } from '../services/abbiWayApi';
import { dateWithNoTimeDashed } from '../utils/date';
import { Button } from '../components/Button';
import { ContentView } from '../components/ContentView';
import { PromptText } from '../components/PromptText';
import { theme } from '../theme';
const styles = StyleSheet.create({
    container: {
        flex: 1,
        minHeight: 0,
        padding: 24,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    section: {
        marginBottom: 24,
    },
    formBlock: {
        flexShrink: 0,
    },
    resultSection: {
        flex: 1,
        minHeight: 0,
        marginBottom: 0,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 8,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 8,
    },
    label: {
        width: 120,
        fontSize: 16,
    },
    pickerContainer: {
        borderWidth: 1,
        borderColor: theme.colors.controlInactive,
        borderRadius: 8,
        minHeight: 40,
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: theme.colors.textOnPrimary,
        flex: 1,
        maxWidth: 320,
    },
    clientsRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: 16,
        marginTop: 8,
    },
    clientColumn: {
        flex: 1,
        minWidth: 0,
    },
    clientColumnLabel: {
        fontSize: 16,
        marginBottom: 6,
    },
    pickerContainerColumn: {
        borderWidth: 1,
        borderColor: theme.colors.controlInactive,
        borderRadius: 8,
        minHeight: 40,
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: theme.colors.textOnPrimary,
        flex: 1,
    },
    pickerList: {
        maxHeight: 200,
    },
    pickerItem: {
        paddingVertical: 8,
        paddingHorizontal: 4,
    },
    pickerItemSelected: {
        backgroundColor: theme.colors.borderDefault,
    },
    pickerItemText: {
        fontSize: 16,
    },
    dateInput: {
        borderWidth: 1,
        borderColor: theme.colors.controlInactive,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 8,
        fontSize: 16,
        minWidth: 160,
    },
    generateButton: {
        marginTop: 16,
        marginBottom: 24,
    },
    resultContainer: {
        borderWidth: 1,
        borderColor: theme.colors.borderSubtle,
        borderRadius: 8,
        padding: 16,
        backgroundColor: theme.colors.surfaceElevatedMuted,
        flex: 1,
        minHeight: 0,
    },
    resultScroll: {
        flex: 1,
        minHeight: 0,
    },
    resultScrollContent: {
        flexGrow: 1,
        paddingBottom: 16,
    },
    resultText: {
        fontFamily: 'monospace',
        fontSize: 12,
    },
    resultActions: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 12,
    },
    errorText: {
        color: theme.colors.generateProgramError,
        marginTop: 8,
    },
});
function formatUserLabel(u) {
    return [u.firstName, u.lastName].filter(Boolean).join(' ') || `User ${u.id}`;
}
function templateFromGender(user) {
    const gender = user?.profile?.gender;
    return gender === 'F' ? 'women' : 'men';
}
export const GenerateProgramScreen = () => {
    const { height: windowHeight } = useWindowDimensions();
    const route = useRoute();
    const selectedUserIdFromRoute = route.params?.selectedUserId;
    const { data: me } = useGetMeQuery();
    const { data: usersResponse } = useGetUsersQuery({}, { refetchOnMountOrArgChange: true });
    const [generateProgram, { data: result, error, isLoading }] = useGenerateProgramMutation();
    const [client1Id, setClient1Id] = useState(null);
    const [client2Id, setClient2Id] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [copyFeedback, setCopyFeedback] = useState(false);
    const users = useMemo(() => {
        if (!usersResponse?.data) {
            return [];
        }
        return usersResponse.data.filter((u) => !u.deletedAt && u.role?.name !== 'Admin');
    }, [usersResponse]);
    useEffect(() => {
        if (selectedUserIdFromRoute != null &&
            users.some((u) => u.id === selectedUserIdFromRoute)) {
            setClient1Id(selectedUserIdFromRoute);
        }
    }, [selectedUserIdFromRoute, users]);
    const canGenerate = client1Id != null &&
        (client2Id == null || (client2Id != null && client1Id !== client2Id));
    const handleGenerate = () => {
        if (!canGenerate) {
            return;
        }
        const userIds = client2Id != null ? [client1Id, client2Id] : [client1Id];
        const client1 = users.find((u) => u.id === client1Id);
        const template = client2Id == null && client1
            ? templateFromGender(client1)
            : undefined;
        generateProgram({
            userIds,
            template,
            startDate: startDate.trim() || undefined,
        });
    };
    const handleDownload = () => {
        if (!result?.markdown) {
            return;
        }
        const client1 = users.find((u) => u.id === client1Id);
        const client2 = client2Id != null ? users.find((u) => u.id === client2Id) : null;
        const names = client2
            ? `${client1?.firstName ?? 'A'}-and-${client2?.firstName ?? 'B'}`
            : `${client1?.firstName ?? 'Client'}-${client1?.lastName ?? 'Program'}`;
        const safe = names.replace(/\s+/g, '-');
        // Local date (not UTC) so the filename matches the admin's calendar day.
        const date = dateWithNoTimeDashed(new Date());
        const filename = `${safe}-Lifestyle-Program-${date}.md`;
        const blob = new Blob([result.markdown], {
            type: 'text/markdown',
            lastModified: Date.now(),
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };
    const handleCopy = async () => {
        if (!result?.markdown) {
            return;
        }
        try {
            await navigator.clipboard.writeText(result.markdown);
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000);
        }
        catch {
            setCopyFeedback(false);
        }
    };
    if (me == null) {
        return (<ContentView>
        <PromptText prompt="Please sign in."/>
      </ContentView>);
    }
    if (me.role?.name !== 'Admin') {
        return (<ContentView>
        <PromptText prompt="You do not have access to this page."/>
      </ContentView>);
    }
    const hasResult = result?.markdown != null && !isLoading;
    const formContent = (<>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Clients</Text>
          <View style={styles.clientsRow}>
            <View style={styles.clientColumn}>
              <Text style={styles.clientColumnLabel}>Client 1</Text>
              <View style={styles.pickerContainerColumn}>
                <ScrollView style={styles.pickerList} nestedScrollEnabled>
                  {users.map((u) => (<TouchableOpacity key={u.id} style={[
                styles.pickerItem,
                client1Id === u.id && styles.pickerItemSelected,
            ]} onPress={() => setClient1Id(u.id)}>
                      <Text style={styles.pickerItemText}>
                        {formatUserLabel(u)}
                      </Text>
                    </TouchableOpacity>))}
                </ScrollView>
              </View>
            </View>
            <View style={styles.clientColumn}>
              <Text style={styles.clientColumnLabel}>
                Client 2 (optional)
              </Text>
              <View style={styles.pickerContainerColumn}>
                <ScrollView style={styles.pickerList} nestedScrollEnabled>
                  <TouchableOpacity style={[
            styles.pickerItem,
            client2Id === null && styles.pickerItemSelected,
        ]} onPress={() => setClient2Id(null)}>
                    <Text style={styles.pickerItemText}>
                      — One client —
                    </Text>
                  </TouchableOpacity>
                  {users.map((u) => (<TouchableOpacity key={u.id} style={[
                styles.pickerItem,
                client2Id === u.id && styles.pickerItemSelected,
            ]} onPress={() => setClient2Id(u.id)}>
                      <Text style={styles.pickerItemText}>
                        {formatUserLabel(u)}
                      </Text>
                    </TouchableOpacity>))}
                </ScrollView>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Start date (optional)</Text>
          <TextInput style={styles.dateInput} placeholder="YYYY-MM-DD" value={startDate} onChangeText={setStartDate}/>
        </View>

        <Button title={isLoading ? 'Generating…' : 'Generate program'} onPress={handleGenerate} loading={isLoading} disabled={!canGenerate} style={styles.generateButton}/>

        {error && (<Text style={styles.errorText}>
            {('data' in error && error.data?.message) ||
                'Generation failed. Please try again.'}
          </Text>)}
      </>);
    const resultBlock = hasResult && (<View style={[styles.section, styles.resultSection]}>
      <Text style={styles.sectionTitle}>Generated program</Text>
      <View style={styles.resultContainer}>
        <ScrollView style={styles.resultScroll} contentContainerStyle={styles.resultScrollContent}>
          <Text style={styles.resultText} selectable>
            {result.markdown}
          </Text>
        </ScrollView>
        <View style={styles.resultActions}>
          <Button title="Download .md" onPress={handleDownload}/>
          <Button title={copyFeedback ? 'Copied!' : 'Copy to clipboard'} onPress={handleCopy}/>
        </View>
      </View>
    </View>);
    return (<ContentView>
      <View style={[
            styles.container,
            hasResult && { height: windowHeight },
        ]}>
        {hasResult ? (<>
            <View style={styles.formBlock}>{formContent}</View>
            {resultBlock}
          </>) : (<ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
            {formContent}
          </ScrollView>)}
      </View>
    </ContentView>);
};
